import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from './AuthContext';
import { Button } from '../components/ui/button';
import { isInCfCooldown, apiRequest } from '../api/apiClient';

// Idle timeouts (P0 UX improvement 2026-02-23 — Wayne's call after the
// "logged out too early" live report). Access-token TTL (60min) and
// refresh-token TTL (7 days) are unchanged — only the FRONTEND idle
// warning window moves. The longer untrusted-device window matches how
// Casting Calls UK is actually used: members read long casting briefs,
// profiles, messages and production details without clicking for many
// minutes at a stretch.
const IDLE_MS_DEFAULT = 60 * 60 * 1000;     // 60 minutes — default (was 20)
const IDLE_MS_TRUSTED = 12 * 60 * 60 * 1000; // 12 hours — "Keep me signed in" devices
const WARNING_MS = 60 * 1000;        // 60-second countdown
const TICK_MS    = 1_000;
const ACTIVITY_DEBOUNCE_MS = 1_000;  // collapse bursty events
const SESSION_FLAG_KEY = 'ccuk_session_timeout_reason';
const TRUSTED_DEVICE_KEY = 'ccuk_trusted_device';

/** Read the per-device trust flag. Trusted devices get a 12-hour idle
 *  window instead of 60 minutes. Set by LoginPage when the user ticks
 *  "Keep me signed in on this device". Persists across sessions until
 *  the user manually clears localStorage or unticks the box on a
 *  future login. */
function getIdleMs() {
  try {
    if (localStorage.getItem(TRUSTED_DEVICE_KEY) === '1') return IDLE_MS_TRUSTED;
  } catch { /* private mode */ }
  return IDLE_MS_DEFAULT;
}

const SessionTimeoutContext = createContext(null);

/**
 * Centralised inactivity-logout flow.
 *
 * Behaviour:
 *   • Tracks mousemove / mousedown / click / keydown / scroll / touchstart
 *     (debounced 1s) plus window focus → resets the idle timer.
 *   • After IDLE_MS of no activity → show the warning modal.
 *   • Modal counts down WARNING_MS; "Stay signed in" closes + validates
 *     the session via /auth/me and re-arms the timer; "Log out now"
 *     short-circuits to logout. Reaching zero auto-logs out.
 *   • Persists `SESSION_FLAG_KEY=inactivity` so the login page can
 *     surface "You were signed out due to inactivity. Please sign in
 *     again." after the redirect.
 *   • Pauses the timer when `isInCfCooldown()` is true (active live
 *     call / Record Video / voice note / upload — anything that takes
 *     the media-session lock) so mid-recording sign-outs cannot happen.
 *   • Single global instance — one timer, one modal. Re-arms cleanly
 *     on login, clears cleanly on logout. Never runs while logged out.
 */
export function SessionTimeoutProvider({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_MS / 1000);
  const [staying, setStaying] = useState(false);

  // Refs hold timer ids so the activity handler can re-arm without
  // re-creating the effect on every state change.
  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());
  const debounceRef = useRef(null);
  const showWarningRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
  }, []);

  const armIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // If the user is in an active media session (call / Record Video
      // / voice note / upload), defer the warning — re-check in 30s.
      // This prevents mid-action sign-outs without weakening auth.
      if (isInCfCooldown()) {
        armIdleTimer();
        return;
      }
      setSecondsLeft(WARNING_MS / 1000);
      setShowWarning(true);
      showWarningRef.current = true;
      // Countdown ticker. Side-effects (logout / navigate) live in a
      // dedicated effect below — the updater stays pure to keep
      // React StrictMode + concurrent features happy.
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = setInterval(() => {
        setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
      }, TICK_MS);
    }, getIdleMs());
  }, []);

  // Watch for the countdown reaching zero and run the auto-logout
  // side-effects HERE (outside any state updater). Keeps the updater
  // pure and resolves the React "Cannot update a component while
  // rendering a different component" warning the testing agent saw.
  useEffect(() => {
    if (!showWarning || secondsLeft > 0) return;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setShowWarning(false);
    showWarningRef.current = false;
    try { sessionStorage.setItem(SESSION_FLAG_KEY, 'inactivity'); } catch { /* private mode */ }
    (async () => {
      try { await logout?.(); } catch { /* swallow */ }
      try { navigate('/login', { replace: true, state: { reason: 'inactivity' } }); }
      catch { /* navigation may be unmounted */ }
    })();
  }, [secondsLeft, showWarning, logout, navigate]);

  const onActivity = useCallback(() => {
    // While the warning is showing the user must click "Stay signed
    // in" — random background movement should NOT silently dismiss
    // the warning, otherwise it would be possible to never see it.
    if (showWarningRef.current) return;
    if (debounceRef.current) return;
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      lastActivityAtRef.current = Date.now();
      armIdleTimer();
    }, ACTIVITY_DEBOUNCE_MS);
  }, [armIdleTimer]);

  const handleStaySignedIn = useCallback(async () => {
    setStaying(true);
    try {
      // Validate session is still alive. If the token already expired
      // on the backend, this returns 401 which the existing apiClient
      // interceptor turns into an `auth:unauthorized` event → redirect.
      await apiRequest.get('/api/auth/me');
    } catch {
      // Transient errors are already swallowed by the CF shield; real
      // 401 will redirect via the existing AuthContext handler.
    } finally {
      setStaying(false);
      setShowWarning(false);
      showWarningRef.current = false;
      if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
      lastActivityAtRef.current = Date.now();
      armIdleTimer();
    }
  }, [armIdleTimer]);

  const handleLogoutNow = useCallback(async () => {
    setShowWarning(false);
    showWarningRef.current = false;
    clearTimers();
    try { sessionStorage.removeItem(SESSION_FLAG_KEY); } catch { /* ignore */ }
    await logout?.();
    try { navigate('/login', { replace: true }); } catch { /* ignore */ }
  }, [clearTimers, logout, navigate]);

  // Bind / unbind activity listeners exactly while a user is signed in.
  useEffect(() => {
    if (!user) {
      // Logged out — make sure everything is torn down.
      clearTimers();
      setShowWarning(false);
      showWarningRef.current = false;
      return undefined;
    }

    const events = ['mousemove', 'mousedown', 'click', 'keydown', 'scroll', 'touchstart'];
    const opts = { passive: true, capture: true };
    events.forEach((ev) => window.addEventListener(ev, onActivity, opts));
    const onFocus = () => { onActivity(); };
    window.addEventListener('focus', onFocus);

    armIdleTimer();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity, opts));
      window.removeEventListener('focus', onFocus);
      clearTimers();
    };
  }, [user, onActivity, armIdleTimer, clearTimers]);

  // Accessibility — focus the primary action when the modal opens.
  const primaryBtnRef = useRef(null);
  useEffect(() => {
    if (showWarning && primaryBtnRef.current) {
      try { primaryBtnRef.current.focus(); } catch { /* ignore */ }
    }
  }, [showWarning]);

  return (
    <SessionTimeoutContext.Provider value={{ showWarning, secondsLeft }}>
      {children}
      {showWarning && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-ccuk-mobile-dialog-root="true"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="session-timeout-title"
          data-testid="session-timeout-modal"
          onKeyDown={(e) => {
            // Don't let Escape silently dismiss — Wayne explicitly
            // wants the user to make a choice.
            if (e.key === 'Escape') e.preventDefault();
          }}
        >
          <div className="w-full max-w-md rounded-xl bg-theme border border-theme p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-amber-500" />
              </div>
              <h3 id="session-timeout-title" className="text-lg font-semibold text-theme-text">
                Are you still there?
              </h3>
            </div>
            <p className="text-sm text-theme-muted mb-1">
              You&apos;ll be logged out in <strong data-testid="session-timeout-countdown">{secondsLeft}</strong>{' '}
              second{secondsLeft === 1 ? '' : 's'} due to inactivity.
            </p>
            <p className="text-xs text-theme-muted mb-5">
              Click <strong>Stay signed in</strong> to continue your session.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleLogoutNow}
                disabled={staying}
                data-testid="session-timeout-logout-now"
              >
                Log out now
              </Button>
              <Button
                ref={primaryBtnRef}
                onClick={handleStaySignedIn}
                disabled={staying}
                data-testid="session-timeout-stay"
              >
                {staying
                  ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  : null}
                Stay signed in
              </Button>
            </div>
          </div>
        </div>
      )}
    </SessionTimeoutContext.Provider>
  );
}

export function useSessionTimeout() {
  const ctx = useContext(SessionTimeoutContext);
  if (!ctx) return { showWarning: false, secondsLeft: WARNING_MS / 1000 };
  return ctx;
}

export const SESSION_TIMEOUT_FLAG_KEY = SESSION_FLAG_KEY;
export const TRUSTED_DEVICE_FLAG_KEY = TRUSTED_DEVICE_KEY;
